import React from 'react'

interface SkipLinkProps {
  /**
   * The ID of the main content element to skip to
   */
  targetId?: string

  /**
   * Custom label for the skip link
   */
  label?: string
}

/**
 * SkipLink Component
 *
 * Provides a "Skip to main content" link for keyboard users to bypass
 * navigation and jump directly to the main content area.
 *
 * The link is visually hidden until focused, meeting WCAG 2.1 guidelines.
 *
 * @example
 * ```tsx
 * <SkipLink targetId="main-content" />
 * ```
 */
export const SkipLink: React.FC<SkipLinkProps> = ({
  targetId = 'main-content',
  label = 'Skip to main content'
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    const target = document.getElementById(targetId)
    if (target) {
      // Set tabindex to make it focusable
      target.setAttribute('tabindex', '-1')
      target.focus()

      // Remove tabindex after focus
      target.addEventListener(
        'blur',
        () => {
          target.removeAttribute('tabindex')
        },
        { once: true }
      )

      // Scroll to target
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className="
        sr-only
        focus:not-sr-only
        focus:absolute
        focus:top-4
        focus:left-4
        focus:z-[9999]
        focus:px-4
        focus:py-2
        focus:bg-cyan-500
        focus:text-white
        focus:font-medium
        focus:rounded-lg
        focus:shadow-lg
        focus:outline-none
        focus:ring-2
        focus:ring-cyan-400
        focus:ring-offset-2
      "
    >
      {label}
    </a>
  )
}

export default SkipLink
